"use client";

import { useState, useRef } from "react";
import { PageContainer } from "@/components/common/Container";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Search, ChevronLeft, ChevronRight } from "lucide-react";

interface ProcessTableData {
  no: number;
  lot: string;
  qty: string;
}

const processes = [
  { id: "forging", name: "단조", code: "01" },
  { id: "rolling", name: "전조", code: "02" },
  { id: "machining", name: "가공", code: "03" },
  { id: "heat-treatment", name: "열처리", code: "04" },
  { id: "surface-treatment", name: "표면처리", code: "05" },
  { id: "packaging", name: "포장", code: "06" },
];

export default function ResultRegistrationPage() {
  const [selectedProcessType, setSelectedProcessType] = useState<"all" | "individual">("all");
  const [selectedProcesses, setSelectedProcesses] = useState<string[]>([]);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [startX, setStartX] = useState(0);
  const [scrollLeft, setScrollLeft] = useState(0);

  const initialData: ProcessTableData[] = [
    { no: 1, lot: "", qty: "" },
    { no: 2, lot: "", qty: "" },
    { no: 3, lot: "", qty: "" },
    { no: 4, lot: "", qty: "" },
    { no: 5, lot: "", qty: "" },
    { no: 6, lot: "", qty: "" },
  ];

  const getProcessData = (processCode: string): ProcessTableData[] => {
    const baseLot = `251201-${processCode}-001`;
    return initialData.map((row, index) => ({
      ...row,
      lot: index === 0 ? baseLot : "",
      qty: index === 0 ? "2,000" : "",
    }));
  };

  const handleProcessCheckboxChange = (processId: string) => {
    // 개별 공정 모드로 전환
    if (selectedProcessType === "all") {
      setSelectedProcessType("individual");
      setSelectedProcesses([processId]);
    } else {
      if (selectedProcesses.includes(processId)) {
        const newSelected = selectedProcesses.filter((id) => id !== processId);
        setSelectedProcesses(newSelected);
        // 모든 공정이 해제되면 전체공정으로 전환
        if (newSelected.length === 0) {
          setSelectedProcessType("all");
        }
      } else {
        setSelectedProcesses([...selectedProcesses, processId]);
      }
    }
  };

  const handleProcessTypeChange = (type: "all" | "individual") => {
    setSelectedProcessType(type);
    if (type === "all") {
      setSelectedProcesses([]);
    }
  };

  const handleScrollLeft = () => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollBy({
        left: -400,
        behavior: "smooth",
      });
    }
  };

  const handleScrollRight = () => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollBy({
        left: 400,
        behavior: "smooth",
      });
    }
  };

  const handleMouseDown = (e: React.MouseEvent) => {
    if (!scrollContainerRef.current) return;
    setIsDragging(true);
    setStartX(e.pageX - scrollContainerRef.current.offsetLeft);
    setScrollLeft(scrollContainerRef.current.scrollLeft);
    scrollContainerRef.current.style.cursor = "grabbing";
    scrollContainerRef.current.style.userSelect = "none";
  };

  const handleMouseLeave = () => {
    setIsDragging(false);
    if (scrollContainerRef.current) {
      scrollContainerRef.current.style.cursor = "grab";
      scrollContainerRef.current.style.userSelect = "";
    }
  };

  const handleMouseUp = () => {
    setIsDragging(false);
    if (scrollContainerRef.current) {
      scrollContainerRef.current.style.cursor = "grab";
      scrollContainerRef.current.style.userSelect = "";
    }
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (!isDragging || !scrollContainerRef.current) return;
    e.preventDefault();
    const x = e.pageX - scrollContainerRef.current.offsetLeft;
    const walk = (x - startX) * 2; // 스크롤 속도 조절
    scrollContainerRef.current.scrollLeft = scrollLeft - walk;
  };

  // 터치 이벤트 핸들러 (모바일 지원)
  const handleTouchStart = (e: React.TouchEvent) => {
    if (!scrollContainerRef.current) return;
    setIsDragging(true);
    setStartX(e.touches[0].pageX - scrollContainerRef.current.offsetLeft);
    setScrollLeft(scrollContainerRef.current.scrollLeft);
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    if (!isDragging || !scrollContainerRef.current) return;
    const x = e.touches[0].pageX - scrollContainerRef.current.offsetLeft;
    const walk = (x - startX) * 2;
    scrollContainerRef.current.scrollLeft = scrollLeft - walk;
  };

  const handleTouchEnd = () => {
    setIsDragging(false);
  };

  const visibleProcesses =
    selectedProcessType === "all"
      ? processes
      : processes.filter((p) => selectedProcesses.includes(p.id));

  return (
    <PageContainer>
      <main className="flex-1 min-h-0 overflow-y-auto p-4">
        <div className="container mx-auto space-y-4">
          {/* 상단 입력 필드 섹션 */}
          <div className="bg-white dark:bg-card rounded-lg border p-4">
            <h2 className="text-lg font-semibold mb-3 pb-2 border-b">
              생산 계획 정보
            </h2>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
            <div className="space-y-1.5">
              <Label htmlFor="plan-number" className="text-sm font-medium">
                생산계획번호
              </Label>
              <div className="relative">
                <Input
                  id="plan-number"
                  value="2512-0001"
                  readOnly
                  className="pr-10"
                />
                <Button
                  variant="ghost"
                  size="icon"
                  className="absolute right-1 top-1/2 -translate-y-1/2 h-7 w-7"
                >
                  <Search className="h-4 w-4" />
                </Button>
              </div>
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="unique-number" className="text-sm font-medium">
                고유번호
              </Label>
              <Input 
                id="unique-number" 
                value="0001" 
                readOnly 
              />
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="part-number" className="text-sm font-medium">
                품번
              </Label>
              <Input 
                id="part-number" 
                value="A002-1-0001" 
                readOnly 
              />
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="part-name" className="text-sm font-medium">
                품명
              </Label>
              <Input 
                id="part-name" 
                value="Spindle Nut" 
                readOnly 
              />
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="specification" className="text-sm font-medium">
                제품규격
              </Label>
              <Input 
                id="specification" 
                value="12*23P" 
                readOnly 
              />
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="planned-qty" className="text-sm font-medium">
                계획량
              </Label>
              <Input 
                id="planned-qty" 
                value="100,000 EA" 
                readOnly 
              />
            </div>
            </div>
          </div>

          {/* 공정 선택 섹션 */}
          <div className="bg-white dark:bg-card rounded-lg border p-4">
            <h2 className="text-lg font-semibold mb-3 pb-2 border-b">
              공정 선택
            </h2>
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-4">
                <div className="flex items-center gap-1.5">
                  <input
                    type="radio"
                    id="all-processes"
                    name="process-type"
                    checked={selectedProcessType === "all"}
                    onChange={() => handleProcessTypeChange("all")}
                    className="h-4 w-4"
                  />
                  <Label htmlFor="all-processes" className="font-medium cursor-pointer">
                    전체공정
                  </Label>
                </div>
                <div className="flex items-center gap-1.5">
                  <input
                    type="radio"
                    id="individual-processes"
                    name="process-type"
                    checked={selectedProcessType === "individual"}
                    onChange={() => handleProcessTypeChange("individual")}
                    className="h-4 w-4"
                  />
                  <Label htmlFor="individual-processes" className="font-medium cursor-pointer">
                    개별공정
                  </Label>
                </div>
              </div>

              <div className="flex items-center gap-3 flex-wrap">
                {processes.map((process) => (
                  <div key={process.id} className="flex items-center gap-1.5">
                    <Checkbox
                      id={process.id}
                      checked={
                        selectedProcessType === "all" ||
                        selectedProcesses.includes(process.id)
                      }
                      disabled={selectedProcessType === "all"}
                      onCheckedChange={() => handleProcessCheckboxChange(process.id)}
                    />
                    <Label
                      htmlFor={process.id}
                      className="font-medium cursor-pointer disabled:opacity-50"
                    >
                      {process.name}
                    </Label>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* 공정 테이블 섹션 */}
          <div className="bg-white dark:bg-card rounded-lg border">
            <div className="px-4 pt-4 pb-3 border-b">
              <h2 className="text-lg font-semibold">
                공정별 실적 정보
              </h2>
            </div>
            <div className="relative py-3">
              <div className="relative -mx-4 px-4">
                <div className="flex items-center gap-3 relative">
                <Button
                  variant="outline"
                  size="icon"
                  onClick={handleScrollLeft}
                  className="shrink-0 z-10 ml-2"
                  aria-label="이전 공정 보기"
                >
                  <ChevronLeft className="h-4 w-4" />
                </Button>

                <div
                  ref={scrollContainerRef}
                  className="flex gap-3 overflow-x-auto scrollbar-hide flex-1 cursor-grab active:cursor-grabbing select-none"
                  onMouseDown={handleMouseDown}
                  onMouseLeave={handleMouseLeave}
                  onMouseUp={handleMouseUp}
                  onMouseMove={handleMouseMove}
                  onTouchStart={handleTouchStart}
                  onTouchMove={handleTouchMove}
                  onTouchEnd={handleTouchEnd}
                >
                {visibleProcesses.map((process) => {
                  const processData = getProcessData(process.code);
                  return (
                    <div
                      key={process.id}
                      className="border rounded-lg overflow-hidden min-w-[280px] shrink-0"
                    >
                      <div className="bg-brand-600 dark:bg-brand-700 px-3 py-2 border-b">
                        <h3 className="font-semibold text-sm text-white">{process.name}</h3>
                      </div>
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead className="w-12 font-semibold border-r text-center">no</TableHead>
                            <TableHead className="font-semibold border-r text-center">LOT</TableHead>
                            <TableHead className="w-24 font-semibold text-center">Qty</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {processData.map((row, index) => (
                            <TableRow 
                              key={index}
                            >
                              <TableCell className="text-center font-medium border-r">
                                {row.no}
                              </TableCell>
                              <TableCell className="font-mono text-xs border-r">
                                {row.lot || <span className="text-muted-foreground">-</span>}
                              </TableCell>
                              <TableCell className="text-right font-medium">
                                {row.qty || <span className="text-muted-foreground">-</span>}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                  );
                })}
              </div>

                  <Button
                    variant="outline"
                    size="icon"
                    onClick={handleScrollRight}
                    className="shrink-0 z-10 mr-2"
                    aria-label="다음 공정 보기"
                  >
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
    </PageContainer>
  );
}
